// src/utils/NavigationBarManager.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Centralized manager for Android Status Bar + Navigation Bar appearance.
//
// ROBUSTNESS LAYERS:
//  1. Init guard (single initialization, promise-based)
//  2. Deduplication (skip native calls if values unchanged)
//  3. Visibility handler (restore bars when app returns from background)
//  4. Native nav bar control (AndroidNavigationBar API, CSS fallback)
//  5. Graceful degradation (every failure path is logged, never silent)
//
// STATUS BAR COLOR RULES:
//  All pages → Gold #D4AF37 + dark icons (light theme)
//  Dark mode  → #121212 + light icons
//
// NAVIGATION BAR RULES:
//  Light theme  → White #FFFFFF   (CSS variable + native where available)
//  Dark theme   → #121212
//  API 29+ styles.xml sets transparent; CSS variable paints WebView area
// ─────────────────────────────────────────────────────────────────────────────

import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

// ── Brand constants ──────────────────────────────────────────────────────────
const COLOR_GOLD = '#D4AF37';
const COLOR_DARK = '#121212';
const COLOR_NAV_LIGHT = '#FFFFFF';
const COLOR_NAV_DARK = '#121212';

// ── Internal state ───────────────────────────────────────────────────────────
let initPromise = null;
let isInitialized = false;
let lastSet = {
  statusColor: null,
  statusStyle: null,
  navColor: null,
};
let visibilityHandlerRegistered = false;

// ── Helpers ──────────────────────────────────────────────────────────────────
function isDarkMode() {
  return document.documentElement.classList.contains('dark');
}

function resolvePageConfig(routePath) {
  if (isDarkMode()) {
    return { color: COLOR_DARK, style: Style.Light };
  }
  const path = (routePath || (typeof window !== 'undefined' ? window.location.pathname : '')).toLowerCase();
  const isDashboard = path === '/dashboard' || path === '/' || path === '';
  if (isDashboard) {
    return { color: COLOR_GOLD, style: Style.Dark };
  }
  return { color: COLOR_NAV_LIGHT, style: Style.Light };
}

function resolveNavColor() {
  return isDarkMode() ? COLOR_NAV_DARK : COLOR_NAV_LIGHT;
}

async function safeCallAsync(fn, context) {
  try {
    return await fn();
  } catch (err) {
    console.warn(`[NavigationBarManager] ${context}:`, err?.message || err);
    return undefined;
  }
}

// ── Native nav bar setter (Android only) ────────────────────────────────────
async function setNativeNavColor(color) {
  if (!Capacitor.isNativePlatform()) return;

  if (typeof window !== 'undefined' && window.AndroidNavigationBar) {
    await safeCallAsync(
      () => window.AndroidNavigationBar.setNavigationBarColor({ color }),
      'AndroidNavigationBar.setNavigationBarColor'
    );
    return;
  }

  const navColorProp = window?.AndroidInterface?.navigationBarColor;
  if (typeof navColorProp === 'function') {
    await safeCallAsync(() => navColorProp(color), 'AndroidInterface.navigationBarColor');
  }
}

// ── Core: apply status bar ──────────────────────────────────────────────────
async function applyStatusBar({ color, style }) {
  if (lastSet.statusColor === color && lastSet.statusStyle === style) return;

  await safeCallAsync(() => StatusBar.setStyle({ style }), 'StatusBar.setStyle');
  await safeCallAsync(() => StatusBar.setBackgroundColor({ color }), 'StatusBar.setBackgroundColor');

  lastSet.statusColor = color;
  lastSet.statusStyle = style;

  document.documentElement.style.setProperty('--status-bar-bg', color);
  document.documentElement.style.setProperty(
    '--status-bar-is-dark',
    color === COLOR_DARK ? '1' : '0'
  );
}

// ── Core: apply navigation bar ──────────────────────────────────────────────
async function applyNavigationBar(color) {
  if (lastSet.navColor === color) return;

  await setNativeNavColor(color);

  lastSet.navColor = color;

  document.documentElement.style.setProperty('--nav-bar-bg', color);

  const needsLightIcons = color !== COLOR_NAV_LIGHT;
  document.documentElement.style.setProperty(
    '--nav-bar-icons-light',
    needsLightIcons ? '1' : '0'
  );
}

// ── Public API ──────────────────────────────────────────────────────────────
export class NavigationBarManager {

  static async initialize() {
    if (isInitialized) return initPromise;

    initPromise = (async () => {
      if (!Capacitor.isNativePlatform()) {
        isInitialized = true;
        return;
      }

      await safeCallAsync(
        () => StatusBar.setOverlaysWebView({ overlay: false }),
        'StatusBar.setOverlaysWebView'
      );

      const config = resolvePageConfig(window.location.pathname);
      const navColor = resolveNavColor();
      await applyStatusBar(config);
      await applyNavigationBar(navColor);

      if (!visibilityHandlerRegistered) {
        visibilityHandlerRegistered = true;
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            safeCallAsync(
              () => NavigationBarManager.setForPage(window.location.pathname),
              'visibilitychange restore'
            );
          }
        });
      }

      isInitialized = true;
      console.log('[NavigationBarManager] initialized');
    })();

    return initPromise;
  }

  static async setForPage(routePath) {
    if (!Capacitor.isNativePlatform()) return;

    const { color, style } = resolvePageConfig(routePath);
    const navColor = resolveNavColor();

    await applyStatusBar({ color, style });
    await applyNavigationBar(navColor);
  }

  static async showStatusBar() {
    if (!Capacitor.isNativePlatform()) return;

    await safeCallAsync(() => StatusBar.show(), 'StatusBar.show');
    await safeCallAsync(
      () => StatusBar.setOverlaysWebView({ overlay: false }),
      'StatusBar.setOverlaysWebView'
    );
    await NavigationBarManager.setForPage(window.location.pathname);
  }

  static async setBackgroundColor(color) {
    if (!Capacitor.isNativePlatform()) return;
    await safeCallAsync(() => StatusBar.setBackgroundColor({ color }), 'StatusBar.setBackgroundColor');
  }

  static async enableFullScreen() {
    if (!Capacitor.isNativePlatform()) return;
    await safeCallAsync(() => StatusBar.hide(), 'StatusBar.hide');
  }

  static async onThemeChange() {
    if (!Capacitor.isNativePlatform()) return;
    await NavigationBarManager.setForPage(window.location.pathname);
  }

  static getLastSet() {
    return { ...lastSet };
  }

  static resetState() {
    lastSet = { statusColor: null, statusStyle: null, navColor: null };
    isInitialized = false;
    initPromise = null;
  }
}
